import { FlightDeskConfig, OrganizationInfo, getApiUrl } from './config'

/**
 * Simple GraphQL client for CLI operations.
 * We avoid using the full Apollo client to keep the CLI bundle small.
 */
export class FlightDeskAPI {
  private readonly apiUrl: string
  private readonly apiKey: string
  private readonly organizationId: string

  constructor(config: FlightDeskConfig, org: OrganizationInfo) {
    if (!config.apiKey) {
      throw new Error('API key is required. Run: flightdesk login')
    }
    this.apiUrl = getApiUrl()
    this.apiKey = config.apiKey
    this.organizationId = org.id
  }

  /**
   * Create an API client from config, using the active organization
   */
  static fromConfig(config: FlightDeskConfig, org: OrganizationInfo): FlightDeskAPI {
    return new FlightDeskAPI(config, org)
  }

  async graphql<T = unknown>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const verbose = process.env.FLIGHTDESK_DEBUG === '1'

    if (verbose) {
      console.log('\n--- GraphQL Request ---')
      console.log('URL:', `${this.apiUrl}/graphql`)
      console.log('Variables:', JSON.stringify(variables, null, 2))
    }

    const response = await fetch(`${this.apiUrl}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({ query, variables }),
    })

    if (!response.ok) {
      const body = await response.text()
      if (verbose) {
        console.log('--- Error Response ---')
        console.log('Status:', response.status, response.statusText)
        console.log('Body:', body)
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}${verbose ? '' : ' (set FLIGHTDESK_DEBUG=1 for details)'}`)
    }

    const result = await response.json() as { data?: T; errors?: Array<{ message: string }> }

    if (verbose) {
      console.log('--- Response ---')
      console.log(JSON.stringify(result, null, 2))
    }

    if (result.errors && result.errors.length > 0) {
      throw new Error(`GraphQL error: ${result.errors.map(e => e.message).join(', ')}`)
    }

    return result.data as T
  }

  // ============================================================================
  // Task Operations
  // ============================================================================

  async createTask(input: {
    projectId: string
    title: string
    description?: string
    subprojectId?: string
  }) {
    const query = `
      mutation CreateTask($input: UserCreateTaskInput!) {
        userCreateTask(input: $input) {
          id
          title
          status
          createdAt
        }
      }
    `

    const result = await this.graphql<{ userCreateTask: { id: string; title: string; status: string; createdAt: string } }>(query, { input })
    return result.userCreateTask
  }

  async updateTask(taskId: string, input: {
    status?: string
    branchName?: string
    prUrl?: string
    prNumber?: number
    sessionViewUrl?: string
    sessionTeleportId?: string
  }) {
    const query = `
      mutation UpdateTask($taskId: String!, $input: UserUpdateTaskInput!) {
        userUpdateTask(taskId: $taskId, input: $input) {
          id
          title
          status
          branchName
          prUrl
          prNumber
          sessionViewUrl
          sessionTeleportId
        }
      }
    `

    const result = await this.graphql<{ userUpdateTask: { id: string; title: string; status: string; branchName?: string; prUrl?: string; prNumber?: number; sessionViewUrl?: string; sessionTeleportId?: string } }>(query, { taskId, input })
    return result.userUpdateTask
  }

  async registerSession(taskId: string, input: { viewUrl: string; teleportId: string }) {
    const query = `
      mutation RegisterSession($taskId: String!, $input: RegisterSessionInput!) {
        userRegisterSession(taskId: $taskId, input: $input) {
          id
          title
          status
          sessionViewUrl
          sessionTeleportId
        }
      }
    `

    const result = await this.graphql<{ userRegisterSession: { id: string; title: string; status: string; sessionViewUrl?: string; sessionTeleportId?: string } }>(query, { taskId, input })
    return result.userRegisterSession
  }

  async getTask(taskId: string) {
    const query = `
      query GetTask($taskId: String!) {
        userTask(taskId: $taskId) {
          id
          title
          description
          status
          branchName
          prUrl
          prNumber
          sessionViewUrl
          sessionTeleportId
          createdAt
          updatedAt
          project {
            id
            name
            githubRepo
          }
        }
      }
    `

    const result = await this.graphql<{ userTask: unknown }>(query, { taskId })
    return result.userTask
  }

  async listTasks(options?: { projectId?: string; status?: string | string[]; limit?: number }) {
    const query = `
      query ListTasks($input: ListTasksInput!) {
        userTasks(input: $input) {
          id
          title
          status
          branchName
          prUrl
          sessionViewUrl
          createdAt
          project {
            id
            name
          }
        }
      }
    `

    // Build the input object - all fields are optional now
    const input: { projectId?: string; status?: string[]; limit?: number } = {}
    if (options?.projectId) {
      input.projectId = options.projectId
    }
    if (options?.status) {
      input.status = Array.isArray(options.status) ? options.status : [options.status]
    }
    if (options?.limit) {
      input.limit = options.limit
    }

    const result = await this.graphql<{ userTasks: Array<{ id: string; title: string; status: string; branchName?: string; prUrl?: string; createdAt: string; project: { id: string; name: string } }> }>(query, { input })
    return result.userTasks
  }

  async syncTaskChecks(taskId: string) {
    const query = `
      mutation SyncTaskChecks($taskId: String!) {
        userSyncTaskChecks(taskId: $taskId) {
          synced
          checks {
            integration
            reviewer
            status
          }
        }
      }
    `

    const result = await this.graphql<{ userSyncTaskChecks: { synced: number; checks: Array<{ integration: string; reviewer: string; status: string }> } }>(query, { taskId })
    return result.userSyncTaskChecks
  }

  // ============================================================================
  // Project Operations
  // ============================================================================

  async listProjects() {
    const query = `
      query ListProjects($organizationId: String!) {
        userProjects(organizationId: $organizationId) {
          id
          name
          githubRepo
        }
      }
    `

    const result = await this.graphql<{ userProjects: Array<{ id: string; name: string; githubRepo: string }> }>(query, { organizationId: this.organizationId })
    return result.userProjects
  }

  async getProject(projectId: string) {
    const query = `
      query GetProject($projectId: String!) {
        userProject(projectId: $projectId) {
          id
          name
          githubRepo
          tokenTtlHours
        }
      }
    `

    const result = await this.graphql<{ userProject: { id: string; name: string; githubRepo: string; tokenTtlHours: number } }>(query, { projectId })
    return result.userProject
  }

  async createProject(input: {
    name: string
    organizationId: string
    githubConnectionId: string
    githubRepo: string
    teamId?: string
    tokenTtlHours?: number
    previewEnabled?: boolean
  }) {
    const query = `
      mutation CreateProject($input: UserCreateProjectInput!) {
        userCreateProject(input: $input) {
          id
          name
          githubRepo
          organizationId
          createdAt
        }
      }
    `

    const result = await this.graphql<{ userCreateProject: { id: string; name: string; githubRepo: string; organizationId: string; createdAt: string } }>(query, { input })
    return result.userCreateProject
  }

  // ============================================================================
  // Task Token Operations (for agent authentication)
  // ============================================================================

  async createTaskToken(taskId: string) {
    const query = `
      mutation CreateTaskToken($taskId: String!) {
        userCreateTaskToken(taskId: $taskId) {
          token
          expiresAt
        }
      }
    `

    const result = await this.graphql<{ userCreateTaskToken: { token: string; expiresAt: string } }>(query, { taskId })
    return result.userCreateTaskToken
  }

  // ============================================================================
  // Prompt Operations
  // ============================================================================

  async getTaskPrompts(taskId: string) {
    const query = `
      query GetTaskPrompts($taskId: String!) {
        userTaskPrompts(taskId: $taskId) {
          type
          content
          available
          reason
        }
      }
    `

    const result = await this.graphql<{ userTaskPrompts: Array<{ type: string; content: string; available: boolean; reason?: string }> }>(query, { taskId })
    return result.userTaskPrompts
  }

  // ============================================================================
  // Preview Environment Operations
  // ============================================================================

  async getTaskInstance(taskId: string) {
    const query = `
      query GetTaskInstance($taskId: String!) {
        userTaskInstance(taskId: $taskId) {
          id
          taskId
          workerId
          containerId
          previewUrl
          sshHost
          sshPort
          sshUser
          status
          lastActivityAt
          suspendedAt
          createdAt
          updatedAt
          sshConnectionString
          processUrls {
            name
            url
            primary
          }
        }
      }
    `

    const result = await this.graphql<{ userTaskInstance: {
      id: string
      taskId: string
      workerId: string
      containerId: string
      previewUrl: string
      sshHost: string
      sshPort: number
      sshUser: string
      status: string
      lastActivityAt?: string
      suspendedAt?: string
      createdAt: string
      updatedAt: string
      sshConnectionString: string
      processUrls?: Array<{
        name: string
        url: string
        primary?: boolean
      }>
    } | null }>(query, { taskId })
    return result.userTaskInstance
  }

  async getInstanceLogs(taskId: string, lines = 100) {
    const query = `
      query GetInstanceLogs($taskId: String!, $lines: Float) {
        userInstanceLogs(taskId: $taskId, lines: $lines) {
          logs
        }
      }
    `

    const result = await this.graphql<{ userInstanceLogs: { logs: string } }>(query, { taskId, lines })
    return result.userInstanceLogs
  }

  async restartInstance(taskId: string) {
    const query = `
      mutation RestartInstance($taskId: String!) {
        userRestartInstance(taskId: $taskId)
      }
    `

    const result = await this.graphql<{ userRestartInstance: boolean }>(query, { taskId })
    return result.userRestartInstance
  }

  async resumeInstance(taskId: string) {
    const query = `
      mutation ResumeInstance($taskId: String!) {
        userResumeInstance(taskId: $taskId)
      }
    `

    const result = await this.graphql<{ userResumeInstance: boolean }>(query, { taskId })
    return result.userResumeInstance
  }

  async tearDownInstance(taskId: string) {
    const query = `
      mutation TearDownInstance($taskId: String!) {
        userTearDownInstance(taskId: $taskId)
      }
    `

    const result = await this.graphql<{ userTearDownInstance: boolean }>(query, { taskId })
    return result.userTearDownInstance
  }
}

// ============================================================================
// Standalone API functions (for use without full config)
// ============================================================================

export interface UserOrganization {
  organization: {
    id: string
    name: string
  }
}

export interface MeResponse {
  id: string
  emails: Array<{ email: string; primary: boolean }>
  organizations: UserOrganization[]
}

/**
 * Fetch user info and organizations using an API key
 */
export async function fetchUserInfo(apiKey: string, apiUrl = 'https://api.flightdesk.dev'): Promise<MeResponse> {
  const response = await fetch(`${apiUrl}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      query: `{
        me {
          id
          emails {
            email
            primary
          }
          organizations {
            organization {
              id
              name
            }
          }
        }
      }`,
    }),
  })

  if (!response.ok) {
    throw new Error(`API returned ${response.status}`)
  }

  const result = await response.json() as {
    data?: { me?: MeResponse }
    errors?: Array<{ message: string }>
  }

  if (result.errors?.length) {
    throw new Error(result.errors.map(e => e.message).join(', '))
  }

  if (!result.data?.me) {
    throw new Error('Invalid API response')
  }

  return result.data.me
}
